import { KeyboardEvent, MouseEvent, RefObject, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useRoverState, RoverStateReturn } from "reakit/Rover";
import { KEY_ARROW_DOWN, KEY_ENTER, KEY_ESCAPE, KEY_SPACE } from "../constants/keyboardCodes";

enum MENU_ACTIONS {
  OPEN,
  CLOSE,
  TOGGLE,
}

type StateNavProps = {
  isMenuOpen: boolean;
  menuRef?: RefObject<HTMLDivElement>;
  inputRef?: RefObject<HTMLInputElement>;
  rover?: any;
};

type Result = {
  isMenuOpen: boolean;
  menuRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLInputElement>;
  rover: RoverStateReturn;
  onKeyUp: (e: KeyboardEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onItemKeyUp: (e: KeyboardEvent<HTMLElement>) => void;
  onComboBoxKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

export const useKeyboardNav = (initialValues: Pick<StateNavProps, "isMenuOpen">): Result => {
  const keyboardNavReducer = (state: StateNavProps, action: { type: MENU_ACTIONS }): StateNavProps => {
    switch (action.type) {
      case MENU_ACTIONS.OPEN:
        return { isMenuOpen: true };
      case MENU_ACTIONS.CLOSE:
        return { isMenuOpen: false };
      case MENU_ACTIONS.TOGGLE:
        return { isMenuOpen: !state.isMenuOpen };
    }
  };

  const [menuState, dispatch] = useReducer(keyboardNavReducer, initialValues);

  const rover = useRoverState();

  const menuTrigger = useRef<HTMLDivElement>(null);
  const inputTrigger = useRef<HTMLInputElement>(null);

  const [shouldFocusMenuTrigger, setShouldFocusMenuTrigger] = useState(false);
  const [shouldFocusMenuMenuItem, setShouldFocusMenuMenuItem] = useState(false);

  /**
   * This effect places focus on menu trigger or first item in the menu
   */
  useEffect(() => {
    /**
     * In this render:
     * - inputTrigger is focused
     * - menu open was initiated (click, Enter, Space, DownArrow)
     * - menu items are not yet added to DOM
     *   (`rover.stops` list contains references to menu item elements)
     *
     * Set shouldFocusMenuMenuItem to true to notify that on subsequent render
     * menu item will have to get focus.
     */
    if (document.activeElement === inputTrigger.current && menuState.isMenuOpen && rover.stops.length === 0) {
      setShouldFocusMenuMenuItem(true);
      /**
       * In this render:
       * - active element is inputTrigger
       * - menu is open
       * - menu items are already shown
       * - shouldFocusMenuMenuItem is true meaning that
       *   this render came after menu trigger was activated i.e. previous if
       *
       * Set focus on first menu item
       */
    } else if (document.activeElement === inputTrigger.current && menuState.isMenuOpen && rover.stops.length && shouldFocusMenuMenuItem) {
      inputTrigger.current?.focus?.();
      setShouldFocusMenuMenuItem(false);
    } else if (
      /**
       * In this render:
       * - menuTrigger is focused
       * - menu open was initiated (click, Enter, Space, DownArrow)
       * - menu items are not yet added to DOM
       *   (`rover.stops` list contains references to menu item elements)
       *
       * Set shouldFocusMenuMenuItem to true to notify that on subsequent render
       * menu item will have to get focus.
       */
      document.activeElement === menuTrigger.current &&
      menuState.isMenuOpen &&
      rover.stops.length === 0
    ) {
      setShouldFocusMenuMenuItem(true);
      /**
       * In this render:
       * - menu is open
       * - menu items are already shown
       * - shouldFocusMenuMenuItem is true meaning that
       *   this render came after menu trigger was activated i.e. previous if
       *
       * Set focus on first menu item
       */
    } else if (menuState.isMenuOpen && rover.stops.length && shouldFocusMenuMenuItem) {
      rover.stops[0].ref.current?.focus?.();
      setShouldFocusMenuMenuItem(false);
      /**
       * In this render:
       * - Menu close was initiated (ClickAway, Escape, menu item selected)
       * - Menu items are still shown (started transition to remove them)
       * - One of menu items has focus
       *
       * Set shouldFocusMenuTrigger to true to notify that on subsequent render
       * menu trigger will have to get focus.
       */
    } else if (
      !menuState.isMenuOpen &&
      rover.stops.length &&
      rover.stops.filter(({ ref }) => ref.current === document.activeElement).length
    ) {
      setShouldFocusMenuTrigger(true);
      /**
       * In this render:
       * - Menu is closed
       * - Menu items are removed from DOM
       * - shouldFocusMenuTrigger is true meaning that
       *   this render came after menu was closed i.e. previous if
       *
       * Set focus on menu trigger.
       */
    } else if (!menuState.isMenuOpen && rover.stops.length === 0 && shouldFocusMenuTrigger) {
      menuTrigger?.current?.focus();
      setShouldFocusMenuTrigger(false);
    }
  }, [menuState.isMenuOpen, rover.stops.length]);

  return {
    isMenuOpen: menuState.isMenuOpen,
    menuRef: menuTrigger,
    inputRef: inputTrigger,
    onKeyDown: useCallback((e) => {
      if ([KEY_ESCAPE, KEY_SPACE, KEY_ARROW_DOWN].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, []),

    onKeyUp: useCallback((e) => {
      if ([KEY_ENTER, KEY_ARROW_DOWN, KEY_SPACE].includes(e.key)) {
        dispatch({ type: MENU_ACTIONS.TOGGLE });
      } else if (KEY_ESCAPE === e.key) {
        dispatch({ type: MENU_ACTIONS.CLOSE });
      }
      e.preventDefault();
    }, []),

    onItemKeyUp: useCallback((e) => {
      if (KEY_ESCAPE === e.key) {
        dispatch({ type: MENU_ACTIONS.CLOSE });
      }
    }, []),

    onComboBoxKeyDown: useCallback((e) => {
      if ([KEY_ESCAPE, KEY_ARROW_DOWN].includes(e.key)) {
        e.preventDefault();
      }
    }, []),

    onClick: useCallback(() => dispatch({ type: MENU_ACTIONS.TOGGLE }), []),

    open: useCallback(() => dispatch({ type: MENU_ACTIONS.OPEN }), []),

    close: useCallback(() => dispatch({ type: MENU_ACTIONS.CLOSE }), []),

    toggle: useCallback(() => dispatch({ type: MENU_ACTIONS.TOGGLE }), []),

    rover,
  };
};
